import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DATE_YMD } from 'cargo-chief-commons';

export interface AllocationConfigDialogData {
	carrierBusinessName: string;
	carrierId: string;
	projectId: string;
	defaultAllocationWindowMinutes?: number;
	existingAllocation?: {
		id: string;
		allocationType: string;
		allocationValue: number;
		allocationPeriod: string;
		allocationWindowMinutes: number;
		isActive: boolean;
		effectiveStartDate?: Date | string;
		effectiveEndDate?: Date | string;
	};
}

export interface AllocationConfigResult {
	allocationType: string;
	allocationValue: number;
	allocationPeriod: string;
	allocationWindowMinutes: number;
	isActive: boolean;
	// CN-8662: optional effective window (YYYY-MM-DD). Outside it the allocation is skipped.
	effectiveStartDate?: string;
	effectiveEndDate?: string;
}

@Component({
	selector: 'app-allocation-config-dialog',
	templateUrl: './allocation-config-dialog.component.html',
	styleUrls: ['./allocation-config-dialog.component.scss'],
})
export class AllocationConfigDialogComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	carrierBusinessName: string;
	isEditMode: boolean = false;
	isFormSubmitted: boolean = false;

	allocationTypes = [
		{ value: 'PERCENTAGE', label: 'Percentage of loads' },
		{ value: 'FIXED', label: 'Fixed number of loads' },
	];

	allocationPeriods = [
		{ value: 'DAILY', label: 'Daily' },
		{ value: 'WEEKLY', label: 'Weekly' },
		{ value: 'MONTHLY', label: 'Monthly' },
	];

	allocationWindowOptions = [
		{ value: 5, label: '5 minutes' },
		{ value: 10, label: '10 minutes' },
		{ value: 15, label: '15 minutes' },
		{ value: 30, label: '30 minutes' },
		{ value: 60, label: '1 hour' },
		{ value: 120, label: '2 hours' },
	];

	allocationForm: FormGroup;

	showExplanation: boolean = false;
	// CN-8662: effective dates can't be set in the past.
	minEffectiveDate = new Date();

	constructor(
		public dialogRef: MatDialogRef<AllocationConfigDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: AllocationConfigDialogData
	) {
		this.carrierBusinessName = dialogData.carrierBusinessName;
		this.isEditMode = !!dialogData.existingAllocation;

		// Use BA config default if provided, otherwise fallback to 15 minutes
		const defaultWindowMinutes = dialogData.defaultAllocationWindowMinutes || 15;

		this.allocationForm = new FormGroup({
			allocationType: new FormControl('PERCENTAGE', [Validators.required]),
			allocationValue: new FormControl(null, [Validators.required, Validators.min(1)]),
			allocationPeriod: new FormControl('DAILY', [Validators.required]),
			allocationWindowMinutes: new FormControl(defaultWindowMinutes, [Validators.required]),
			isActive: new FormControl(true),
			effectiveStartDate: new FormControl(null),
			effectiveEndDate: new FormControl(null),
		});

		if (this.isEditMode && dialogData.existingAllocation) {
			this.allocationForm.patchValue({
				allocationType: dialogData.existingAllocation.allocationType,
				allocationValue: dialogData.existingAllocation.allocationValue,
				allocationPeriod: dialogData.existingAllocation.allocationPeriod,
				allocationWindowMinutes: dialogData.existingAllocation.allocationWindowMinutes,
				isActive: dialogData.existingAllocation.isActive,
				effectiveStartDate: this.parseLocalDate(dialogData.existingAllocation.effectiveStartDate),
				effectiveEndDate: this.parseLocalDate(dialogData.existingAllocation.effectiveEndDate),
			});
		}
	}

	ngOnInit(): void {
		// Apply initial validators based on current allocation type (handles edit mode)
		this.updateAllocationValueValidators(this.allocationForm.get('allocationType').value);

		// Add dynamic validation based on allocation type changes
		this.allocationForm
			.get('allocationType')
			.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((type) => {
				this.updateAllocationValueValidators(type);
			});
	}

	private updateAllocationValueValidators(type: string): void {
		const valueControl = this.allocationForm.get('allocationValue');

		if (type === 'PERCENTAGE') {
			valueControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
		} else {
			valueControl.setValidators([Validators.required, Validators.min(1)]);
		}

		valueControl.updateValueAndValidity();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get allocationTypeLabel(): string {
		const type = this.allocationForm.get('allocationType').value;
		return type === 'PERCENTAGE' ? '% of loads' : 'loads';
	}

	get valueHint(): string {
		const type = this.allocationForm.get('allocationType').value;
		return type === 'PERCENTAGE' ? 'Enter a value between 1 and 100' : 'Enter the number of loads';
	}

	/**
	 * CN-8662: parse a stored effective date as a LOCAL date. A date-only "YYYY-MM-DD" string passed
	 * to new Date() is interpreted as UTC midnight, which renders as the previous day in negative-offset
	 * timezones; build it from components in local time so the picker shows the intended day.
	 */
	private parseLocalDate(value: Date | string | null | undefined): Date | null {
		if (!value) {
			return null;
		}
		if (typeof value === 'string') {
			const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
			if (match) {
				return new Date(+match[1], +match[2] - 1, +match[3]);
			}
		}
		return new Date(value);
	}

	submitForm(): void {
		if (this.allocationForm.invalid) {
			this.allocationForm.markAllAsTouched();
			return;
		}

		const startDate = this.allocationForm.get('effectiveStartDate').value;
		const endDate = this.allocationForm.get('effectiveEndDate').value;
		if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
			this.allocationForm.get('effectiveEndDate').setErrors({ beforeStart: true });
			return;
		}

		this.isFormSubmitted = true;
		const formValue = this.allocationForm.value;
		const result: AllocationConfigResult = {
			allocationType: formValue.allocationType,
			allocationValue: formValue.allocationValue,
			allocationPeriod: formValue.allocationPeriod,
			allocationWindowMinutes: formValue.allocationWindowMinutes,
			isActive: formValue.isActive,
			// CN-8662: send as YYYY-MM-DD (or null) to match the backend's date-only handling.
			effectiveStartDate: startDate ? DATE_YMD(startDate) : null,
			effectiveEndDate: endDate ? DATE_YMD(endDate) : null,
		};
		this.dialogRef.close(result);
	}

	closeDialog(): void {
		this.dialogRef.close(null);
	}
}
